/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, ReactNode, useContext, useEffect } from 'react'
import useCart, {
  BatchColorFailedItem,
  Cart,
  DiscountResult
} from 'hooks/useCart'
import { Item } from 'hooks/useProduct'
import { AuthContext } from 'contexts/AuthContext'

type CartContextType = {
  updateCartItem: (updateData: {
    cartItemId?: string
    productId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string | null
    skuPrefix?: string
    poNumber?: string
  }) => Promise<void>
  clearQuotesUi: () => void
  clearQuote: (quoteId: string) => Promise<void>
  switchToCart: (quoteId: string) => Promise<void>
  getCartDetails: () => {
    discountedAmount: string
    totalPrice: string
    assemblyFee: string
    tax: string
    subTotal: string
    cartItemCount: number
  }
  fetchQuotes: () => Promise<void>
  setDiscountCode: (value: ((prevState: string) => string) | string) => void
  clearCartUi: () => void
  setCart: (value: ((prevState: Cart) => Cart) | Cart) => void
  fetchCart: () => Promise<void>
  cart: Cart
  quotes: Cart[]
  deleteQuoteItem: (quoteId: string, cartItemId: string) => Promise<void>
  batchUpdateColor: (
    skuPrefix: string,
    items: Item[],
    cartId: string,
    isQuote?: boolean
  ) => Promise<{
    notFound: BatchColorFailedItem[]
    outOfStock: BatchColorFailedItem[]
  } | null>
  discountCode: string
  updateQuoteItem: (updateData: {
    quoteId: string
    cartItemId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string
    skuPrefix?: string
    poNumber?: string
  }) => Promise<void>
  validateDiscountCode: (code: string) => Promise<void>
  removeDiscount: () => void
  getQuoteDetails: (quote: Cart) => {
    discountedAmount: string
    totalPrice: string
    assemblyFee: string
    tax: string
    subTotal: string
  }
  clearCart: () => Promise<void>
  switchToQuote: () => Promise<void>
  discountResult: DiscountResult | null
  setQuotes: (value: ((prevState: Cart[]) => Cart[]) | Cart[]) => void
  deleteCartItem: (cartItemId: string) => Promise<void>
  downloadQuotePdf: (quoteId: string) => Promise<void>
  isCartReady: boolean
}

const defaultCartContextValue: CartContextType = {
  cart: {
    id: '',
    poNumber: '',
    items: []
  },
  setCart: () => {
    throw new Error('setCart method not implemented')
  },
  fetchCart: async () => {
    throw new Error('fetchCart method not implemented')
  },
  updateCartItem: async () => {
    throw new Error('updateCartItem method not implemented')
  },
  deleteCartItem: async () => {
    throw new Error('deleteCartItem method not implemented')
  },
  clearCart: async () => {
    throw new Error('clearCart method not implemented')
  },
  clearCartUi: () => {
    throw new Error('clearCartUi method not implemented')
  },
  clearQuotesUi: () => {
    throw new Error('clearQuotesUi method not implemented')
  },
  quotes: [],
  setQuotes: () => {
    throw new Error('setQuotes method not implemented')
  },
  switchToQuote: async () => {
    throw new Error('switchToQuote method not implemented')
  },
  fetchQuotes: async () => {
    throw new Error('fetchQuotes method not implemented')
  },
  updateQuoteItem: async () => {
    throw new Error('updateQuoteItem method not implemented')
  },
  deleteQuoteItem: async () => {
    throw new Error('deleteQuoteItem method not implemented')
  },
  batchUpdateColor: async () => {
    throw new Error('batchUpdateColor method not implemented')
  },
  clearQuote: async () => {
    throw new Error('clearQuote method not implemented')
  },
  downloadQuotePdf: async () => {
    throw new Error('downloadQuotePdf method not implemented')
  },
  switchToCart: async () => {
    throw new Error('switchToCart method not implemented')
  },
  getCartDetails: () => ({
    cartItemCount: 0,
    subTotal: '0.00',
    discountedAmount: '0.00',
    tax: '0.00',
    assemblyFee: '0.00',
    totalPrice: '0.00'
  }),
  getQuoteDetails: () => ({
    subTotal: '0.00',
    tax: '0.00',
    assemblyFee: '0.00',
    totalPrice: '0.00',
    discountedAmount: '0.00'
  }),
  validateDiscountCode: async () => {
    throw new Error('validateDiscountCode method not implemented')
  },
  removeDiscount: () => {
    throw new Error('removeDiscount method not implemented')
  },
  discountCode: '',
  setDiscountCode: () => {
    throw new Error('setDiscountCode method not implemented')
  },
  discountResult: null,
  isCartReady: false
}

export const CartContext = createContext<CartContextType>(
  defaultCartContextValue
)

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const {
    cart,
    setCart,
    fetchCart,
    updateCartItem,
    deleteCartItem,
    clearCart,
    clearCartUi,
    clearQuotesUi,
    quotes,
    setQuotes,
    switchToQuote,
    fetchQuotes,
    updateQuoteItem,
    deleteQuoteItem,
    batchUpdateColor,
    clearQuote,
    downloadQuotePdf,
    switchToCart,
    validateDiscountCode,
    removeDiscount,
    discountCode,
    setDiscountCode,
    discountResult,
    getCartDetails,
    getQuoteDetails,
    isCartReady
  } = useCart()

  const { isAuthenticated } = useContext(AuthContext)
  // Cart and quote data are refreshed when authentication changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAuthenticated) {
      fetchCart()
      fetchQuotes()
    }
  }, [isAuthenticated])

  const contextValue: CartContextType = {
    cart,
    setCart,
    fetchCart,
    updateCartItem,
    deleteCartItem,
    clearCart,
    clearCartUi,
    clearQuotesUi,
    quotes,
    setQuotes,
    switchToQuote,
    fetchQuotes,
    updateQuoteItem,
    deleteQuoteItem,
    batchUpdateColor,
    clearQuote,
    downloadQuotePdf,
    switchToCart,
    getCartDetails,
    getQuoteDetails,
    validateDiscountCode,
    removeDiscount,
    discountCode,
    setDiscountCode,
    discountResult,
    isCartReady
  }

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  )
}
